import React from 'react';
import { Badge } from '../../ui/badge';
import { Card } from '../../ui/card';

export const fmtEur = (v) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`);
export const fmtEurCents = (v) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
export const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '—');
export const fmtDayYear = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
export const fmtHours = (mins) => { const m = Math.round(mins || 0); return m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${String(m % 60).padStart(2, '0')}` : ''}` : `${m} min`; };
export const telHref = (p) => `tel:${String(p || '').replace(/[^+\d]/g, '')}`;

/** Statuts Dolibarr d'un devis (0 brouillon, 1 validé/envoyé, 2 signé, 3 refusé, 4 facturé). */
export const QUOTE_STATUS = {
  0: { label: 'Brouillon', cls: 'bg-secondary text-muted-foreground border-border' },
  1: { label: 'Envoyé', cls: 'bg-[hsl(var(--brand-sky))]/40 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-sky))]' },
  2: { label: 'Signé', cls: 'bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30' },
  3: { label: 'Refusé', cls: 'bg-destructive/10 text-destructive border-destructive/30' },
  4: { label: 'Facturé', cls: 'bg-[hsl(var(--brand-sage))]/30 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-sage))]' },
};

export const LEAD_STATUS = {
  nouveau: { label: 'Nouveau', cls: 'bg-[hsl(var(--brand-apricot))]/20 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-apricot))]/60' },
  'contacté': { label: 'Contacté', cls: 'bg-[hsl(var(--brand-sky))]/40 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-sky))]' },
  en_cours: { label: 'En cours', cls: 'bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border-[hsl(var(--accent))]/40' },
  converti: { label: 'Converti', cls: 'bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30' },
  perdu: { label: 'Perdu', cls: 'bg-secondary text-muted-foreground border-border' },
};

export const TICKET_STATE = {
  open: { label: 'Ouvert', cls: 'bg-[hsl(var(--brand-apricot))]/20 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-apricot))]/60' },
  in_progress: { label: 'En cours', cls: 'bg-[hsl(var(--brand-sky))]/40 text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-sky))]' },
  resolved: { label: 'Résolu', cls: 'bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30' },
};

export function StatusBadge({ map, value, fallback = '—', testId }) {
  const s = map[value];
  return <Badge className={`border text-[10px] whitespace-nowrap ${s ? s.cls : 'bg-secondary text-muted-foreground border-border'}`} data-testid={testId}>{s ? s.label : fallback}</Badge>;
}

/** Indicateur compact du tableau de bord (valeur, libellé, précision, action au clic). */
export function Kpi({ icon: Icon, value, label, hint, tone = 'default', onClick, testId }) {
  const tones = {
    default: 'bg-card',
    alert: 'bg-destructive/5 border-destructive/30',
    warn: 'bg-[hsl(var(--brand-apricot))]/10 border-[hsl(var(--brand-apricot))]/50',
    good: 'bg-[hsl(var(--brand-mint))]/40 border-[hsl(var(--primary))]/20',
  };
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold leading-tight">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <p className="text-2xl font-semibold leading-none mt-2 font-['Space_Grotesk']" data-testid={testId ? `${testId}-value` : undefined}>{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1 truncate">{hint}</p>}
    </>
  );
  const cls = `p-3 text-left w-full ${tones[tone] || tones.default} ${onClick ? 'hover:border-[hsl(var(--primary))]/40 active:scale-[0.99] transition-transform' : ''}`;
  return onClick ? <button type="button" onClick={onClick} data-testid={testId} className="text-left"><Card className={cls}>{inner}</Card></button> : <Card className={cls} data-testid={testId}>{inner}</Card>;
}

export function SectionTitle({ icon: Icon, children, action }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-2">
      <h3 className="text-sm font-semibold flex items-center gap-1.5">{Icon && <Icon className="h-4 w-4 text-[hsl(var(--primary))]" />} {children}</h3>
      {action}
    </div>
  );
}

export function EmptyLine({ children, testId }) {
  return <p className="text-sm text-muted-foreground italic py-2" data-testid={testId}>{children}</p>;
}
